<template>
  <header
    class="sticky top-0 z-50 bg-transparent mx-auto max-w-7xl p-4 sm:px-6 lg:px-8"
  >
    <nav
      class="flex items-center justify-between gap-x-6 border border-[#F4EBFF] rounded-xl bg-white py-3 px-4 sm:px-6"
      aria-label="Global"
    >
      <div class="flex lg:flex-1">
        <a
          href="#"
          class="-m-1.5 p-1.5"
        >
          <span class="sr-only">Designer Tool</span>
          <DesignerToolLogo />
        </a>
      </div>
      <div class="hidden lg:flex lg:gap-x-12">
        <a
          v-for="item in navigation"
          :key="item.name"
          :href="item.href"
          class="text-sm/6 font-semibold text-gray-900"
        >{{ item.name }}</a>
      </div>
      <div class="flex flex-1 items-center justify-end gap-x-6">
        <Link
          :href="route('login')"
          class="hidden text-sm/6 font-semibold text-gray-900 lg:block"
        >
          Log in
        </Link>
        <Link
          :href="route('register')"
          class="hidden lg:block"
        >
          <Button> Sign up </Button>
        </Link>
      </div>
      <div class="flex lg:hidden">
        <button
          type="button"
          class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
          @click="mobileMenuOpen = true"
        >
          <span class="sr-only">Open main menu</span>
          <Bars3Icon
            class="size-6"
            aria-hidden="true"
          />
        </button>
      </div>
    </nav>

    <TransitionRoot
      as="template"
      :show="mobileMenuOpen"
    >
      <Dialog
        :open="mobileMenuOpen"
        class="lg:hidden"
        @close="closeMobileMenu"
      >
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div
            class="fixed inset-0 bg-gray-500/20 backdrop-blur-sm"
          />
        </TransitionChild>

        <TransitionChild
          as="template"
          enter="transform transition ease-in-out duration-300"
          enter-from="translate-x-full"
          enter-to="translate-x-0"
          leave="transform transition ease-in-out duration-200"
          leave-from="translate-x-0"
          leave-to="translate-x-full"
        >
          <DialogPanel
            class="fixed inset-y-0 right-0 z-[60] w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10"
          >
            <div
              class="flex items-center justify-between w-full gap-x-6"
            >
              <Link
                href="/"
                class="-m-1.5 p-1.5"
                @click="handleNavigation('/')"
              >
                <span class="sr-only">Designer Tool</span>
                <DesignerToolLogo />
              </Link>
              <button
                type="button"
                class="-m-2.5 rounded-md p-2.5 text-gray-700"
                @click="closeMobileMenu"
              >
                <span class="sr-only">Close menu</span>
                <XMarkIcon
                  class="size-6"
                  aria-hidden="true"
                />
              </button>
            </div>
            <div class="mt-6 flow-root">
              <div class="-my-6 divide-y divide-gray-500/10">
                <div class="flex flex-col gap-y-2 py-6">
                  <a
                    v-for="item in navigation"
                    :key="item.name"
                    :href="item.href"
                    class="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                    @click="handleNavigation(item.href)"
                  >
                    {{ item.name }}
                  </a>
                </div>
                <div class="flex flex-col gap-y-2 py-6">
                  <Link
                    :href="route('login')"
                    class="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                    @click="
                      handleNavigation(route('login'))
                    "
                  >
                    Log in
                  </Link>
                  <Link
                    :href="route('register')"
                    class="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                    @click="
                      handleNavigation(route('register'))
                    "
                  >
                    Sign up
                  </Link>
                </div>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </TransitionRoot>
  </header>
</template>

<script setup lang="ts">
import { ref } from "vue";
import Button from "@/Components/ui/button/Button.vue";
import {
    Dialog,
    DialogPanel,
    TransitionRoot,
    TransitionChild,
} from "@headlessui/vue";
import { Bars3Icon, XMarkIcon } from "@heroicons/vue/24/outline";
import DesignerToolLogo from "@/Components/Icons/DesignerToolLogo.vue";

import { route } from "ziggy-js";
import { Link } from "@inertiajs/vue3";

const navigation = [
    { name: "Why us", href: "#why-us" },
    { name: "Features", href: "#features" },
];

const mobileMenuOpen = ref(false);

function closeMobileMenu() {
    mobileMenuOpen.value = false;
}

function handleNavigation(href: string) {
    closeMobileMenu();
    // Small delay to ensure the menu is closed before scrolling
    setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
            element.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, 300);
}
</script>
